/**
 * PostgreSQL server-version parsing, shared by the renderer and the main
 * process.
 *
 * Lives here rather than in `usePgServerMajorVersion.ts` (its original home,
 * which still re-exports it) because the main process needs the same rule:
 * `metainfo_declare`'s `kind: "json"` branch gates declaring a TEXT column as
 * JSON on PG 16+, exactly as the filter chip and the declare dialog do, and
 * importing the hook module would drag React Query into the main bundle.
 */

/**
 * Parse the PG major version (e.g. `15`, `16`) out of a `version()` string
 * like `"PostgreSQL 16.2 on ..."`. Returns `null` when the string doesn't
 * match the expected shape — callers should treat that as "unknown" and
 * not block on it.
 */
export function parsePgMajorVersion(versionString: string): number | null {
  const match = versionString.match(/^PostgreSQL\s+(\d+)/iu);
  if (!match) return null;
  const n = parseInt(match[1] ?? '', 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Minimum PostgreSQL server version that supports the `IS JSON` predicate
 * (and therefore the declare-on-text JSON path). Older servers can still use
 * native `json`/`jsonb` columns — this constant only gates declaring a
 * text/varchar column as JSON. Re-exported from `filterUtils.ts`, where the
 * renderer's callers import it from.
 */
export const MIN_PG_MAJOR_VERSION_FOR_DECLARED_JSON = 16;
